import React, { useMemo } from 'react';
import RcTooltip from 'rc-tooltip';
import moment from 'moment';
import { Button, CircularProgress, Popover, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import CustomTypography from '@src/new-components/Typography';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import actions from '@src/redux/actions';
import { COLOR } from '@src/styles/color';
import useUserInfo from '@src/hooks/useTtsUser';
import Tooltip from '@src/components/Tooltip';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { useTranslation } from 'react-i18next';
import { updateProject } from '@src/apis/project';
import {
  AUDIO_MODE,
  BLOCK_STATUS,
  CONVERTING_STATUS,
  PROJECT_PRODUCT,
} from '@src/constants/project';
import { EXPORT_TYPE } from '@src/constants/presentation';
import useExportAudio from '@src/hooks/useExportAudio';
import useExportHistoryEnabled from '@src/hooks/useExportHistoryEnabled';
import apis from '@src/apis';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import { downloadFileByUrl, getFileName } from '@src/utils/file';
import useTrackProjectExported from '@src/hooks/useTrackProjectExported';
import {
  audioExpired,
  buildProjectPayload,
  buildVideoSettingsPayload,
  getAudioNameSrtAudio,
  getDubbingBlockChanged,
  handleDownloadParagraphsZip,
  isBlockChanged,
  sanitizeTitleFile,
} from '@src/services/project';
import ExportRetentionNote from '../ExportPanel/ExportRetentionNote';
import ExportPanel from '../ExportPanel';
import {
  PopoverButton,
  StyledDownloadMenu,
  PopoverMenuItem,
} from './index.style';

const StructureOptions = (product) => {
  if (product === PROJECT_PRODUCT.DUBBING) {
    return [
      { label: 'audioFile', value: 'audioFile' },
      { label: 'srtFile', value: 'srtFile' },
    ];
  }
  return [
    { label: 'singleFile', value: 'singleFile' },
    { label: 'paragraphsZip', value: 'paragraphsZip' },
  ];
};

const FormatOptions = [{ label: 'MP3', value: 'mp3' }];

// Each structure option maps to the backend export type it starts, so only the
// type already running gets blocked. `audioFile` is missing on purpose: that is
// the stored SRT-project audio, a direct download rather than an export.
const STRUCTURE_EXPORT_TYPE = {
  singleFile: EXPORT_TYPE.JOIN_AUDIO,
  paragraphsZip: EXPORT_TYPE.SEPARATE_AUDIO,
  srtFile: EXPORT_TYPE.SRT,
};

const DownloadButtonContent = ({
  showLoadingInTooltip,
  downloadProgress,
  showPercentage,
}) => {
  const { t } = useTranslation();
  const exportHistoryEnabled = useExportHistoryEnabled();
  // eslint-disable-next-line no-nested-ternary
  return showLoadingInTooltip ? (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
      }}
    >
      {showPercentage && (
        <CustomTypography variant="body-16-bold">
          {downloadProgress?.percentage || 0}%
        </CustomTypography>
      )}
      <CircularProgress value={75} className="circular-progress" size="16px" />
    </div>
  ) : (
    t(exportHistoryEnabled ? 'exportFileAction' : 'download')
  );
};

const OverLayComponent = ({
  handleDownload,
  onChangeStructure,
  currentStructure,
  showLoadingInTooltip,
  downloadProgress,
  currentStructureValue,
  showPercentage,
  isExportDisabled = false,
}) => {
  const { t } = useTranslation();
  const [anchorStructure, setAnchorStructure] = React.useState(null);
  const { product, audioSrtExpiredAt, audioType, isGeneratingNarration } =
    useSelector((state) => state.project);

  const { ttsUser } = useUserInfo();
  const userAudioRetentionPeriod = ttsUser.retentionPeriod;

  const handleClickStructure = (e) => {
    setAnchorStructure(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorStructure(null);
  };

  const renderArrow = (anchor) => {
    if (anchor) {
      return <ArrowDropUpRoundedIcon width={24} height={24} />;
    }
    return <ArrowDropDownRoundedIcon width={24} height={24} />;
  };

  const showTooltipAudioSrtExpired =
    audioExpired(audioSrtExpiredAt) &&
    product === PROJECT_PRODUCT.DUBBING &&
    currentStructureValue === 'audioFile';
  const isPresentationGeneratingNarration =
    product === PROJECT_PRODUCT.PRESENTATION && isGeneratingNarration;

  return (
    <StyledDownloadMenu>
      <div className="row">
        <div className="label">
          {product === PROJECT_PRODUCT.DUBBING
            ? t('fileTypeDownload')
            : t('type')}
        </div>
        <PopoverButton active={!!anchorStructure}>
          <Button
            className="custom-button"
            onClick={handleClickStructure}
            disabled={isPresentationGeneratingNarration}
          >
            <span className="title-button">
              {t(currentStructure?.label, { format: audioType.toLowerCase() })}
            </span>
            {renderArrow(anchorStructure)}
          </Button>
          <Popover
            open={Boolean(anchorStructure)}
            anchorEl={anchorStructure}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            className="popover-structure"
            PaperProps={{
              style: { borderRadius: '8px' },
            }}
          >
            <div
              style={{
                padding: '8px 0px',
                border: '1px solid #1E164E52',
                borderRadius: '8px',
              }}
            >
              {StructureOptions(product).map((item) => (
                <PopoverMenuItem
                  onClick={() => {
                    onChangeStructure(item);
                    setAnchorStructure(null);
                  }}
                  active={currentStructure?.value === item?.value}
                  key={item.label}
                >
                  <div className="wrap-label">
                    <Typography variant="body2" className="text">
                      {t(item.label, { format: audioType.toLowerCase() })}
                    </Typography>
                    {currentStructure?.value === item?.value && (
                      <CheckRoundedIcon sx={{ color: COLOR.darkBlue }} />
                    )}
                  </div>
                </PopoverMenuItem>
              ))}
            </div>
          </Popover>
        </PopoverButton>
      </div>
      <ExportRetentionNote />
      <Tooltip
        title={t('noteAudioSrtExpired', {
          numberDays: userAudioRetentionPeriod,
        })}
        arrow
        placement="top"
        disableHoverListener={!showTooltipAudioSrtExpired}
        disableFocusListener={!showTooltipAudioSrtExpired}
      >
        <Button
          className="download-btn"
          variant="contained"
          onClick={() => handleDownload()}
          disabled={
            showLoadingInTooltip ||
            showTooltipAudioSrtExpired ||
            isPresentationGeneratingNarration ||
            isExportDisabled
          }
        >
          <DownloadButtonContent
            showLoadingInTooltip={showLoadingInTooltip}
            downloadProgress={downloadProgress}
            showPercentage={showPercentage}
          />
        </Button>
      </Tooltip>
    </StyledDownloadMenu>
  );
};

const DownloadButton = () => {
  const { getFeatureValue } = useFeatureFlags();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.user);
  const {
    multiBlock,
    title,
    isDownloading,
    voiceDefault,
    audioType,
    currentFilteringStatus,
    currentFilteringVoiceCode,
    product,
    status,
    videoSettings,
    isGeneratingNarration,
  } = useSelector((state) => state.project);
  const audioLinkSrt = useSelector((state) => state.project.audioLinkSrt);
  const trackProjectExport = useTrackProjectExported();
  const videoDubbingAspectRatioEnabled = getFeatureValue(
    FEATURE_KEYS.VIDEO_DUBBING_ASPECT_RATIO,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );
  const videoDubbingBgColorEnabled = getFeatureValue(
    FEATURE_KEYS.VIDEO_DUBBING_BG_COLOR,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );
  const location = useLocation();
  const projectId = location.pathname.split('/')[2];
  const [currentFormat, setCurrentFormat] = React.useState(FormatOptions[0]);
  const [currentStructure, setCurrentStructure] = React.useState(
    StructureOptions(product)[0],
  );
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);

  const closeTooltip = () => setIsTooltipOpen(false);
  const separateAudioExport = useExportAudio({
    projectId,
    exportType: EXPORT_TYPE.SEPARATE_AUDIO,
    onSuccess: closeTooltip,
  });
  const singleFileExport = useExportAudio({
    projectId,
    exportType: EXPORT_TYPE.JOIN_AUDIO,
    onSuccess: closeTooltip,
  });
  const exportHistoryEnabled = useExportHistoryEnabled();
  const srtExport = useExportAudio({
    projectId,
    exportType: EXPORT_TYPE.SRT,
    onSuccess: closeTooltip,
  });

  const [downloadProgress, setDownloadProgress] = React.useState(null);
  const isPresentationGeneratingNarration =
    product === PROJECT_PRODUCT.PRESENTATION && isGeneratingNarration;

  const downloadFileSrtEnabled = getFeatureValue(
    FEATURE_KEYS.DOWNLOAD_FILE_SRT,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const useDownloadSeparateAudioV2 = getFeatureValue(
    FEATURE_KEYS.USE_DOWNLOAD_SEPARATE_AUDIO_V2,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const useDownloadSingleAudioV2 = getFeatureValue(
    FEATURE_KEYS.USE_DOWNLOAD_SINGLE_AUDIO_V2,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const isFilteringBlockStatus = useMemo(
    () => currentFilteringStatus !== BLOCK_STATUS.ALL,
    [currentFilteringStatus],
  );

  const isFilteringBlockVoice = useMemo(
    () => currentFilteringVoiceCode !== '',
    [currentFilteringVoiceCode],
  );

  const handleTooltipVisibleChange = (visible) => {
    setIsTooltipOpen(visible);
  };

  const { blocks, originalBlocks, blockIds, originalBlockIds } =
    multiBlock || {};
  const blockWithsText = Object.values(blocks).filter((block) => block?.text);
  const selectedBlockIds = useSelector(
    (state) => state.project.selectedBlockIds,
  );
  const autoSaveEnabled = useSelector((state) => state.project.autoSaveEnabled);
  const saveProject = async () => {
    try {
      const videoSettingsPayload = buildVideoSettingsPayload({
        aspectRatioEnabled: videoDubbingAspectRatioEnabled,
        backgroundColorEnabled: videoDubbingBgColorEnabled,
        videoSettings,
      });
      const response = await updateProject(
        projectId,
        buildProjectPayload({
          title,
          blockIds: multiBlock?.blockIds || [],
          blocks,
          voiceDefault,
          audioType,
          isPresentation: product === PROJECT_PRODUCT.PRESENTATION,
          videoSettings:
            product === PROJECT_PRODUCT.DUBBING
              ? videoSettingsPayload
              : undefined,
        }),
      );

      return response;
    } catch {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'errorOccurred',
        }),
      );
      return null;
    }
  };

  const hasAudioExpired = blockWithsText.some((block) =>
    audioExpired(block?.audioExpiredAt),
  );
  const blockWithTextChanged = useMemo(
    () =>
      blockWithsText.filter((block) => {
        const withoutExtension = block?.audioLink?.split('.')?.pop();
        const currentBlock = {
          text: block?.text,
          voiceCode: block?.voiceCode,
          speed: block?.speed,
          audioType: withoutExtension,
        };
        const originalBlock = {
          text: originalBlocks[block.id]?.text,
          voiceCode: originalBlocks[block.id]?.voiceCode,
          speed: originalBlocks[block.id]?.speed,
          audioType,
        };
        return isBlockChanged(currentBlock, originalBlock) || !block?.audioLink;
      }),
    [blockWithsText, originalBlocks],
  );
  const isSynthesisInProgress =
    blockWithsText.filter(
      (block) => block.status === CONVERTING_STATUS.IN_PROGRESS,
    ).length > 0;

  const isDisabledBtnDownload =
    !blockWithsText?.length ||
    hasAudioExpired ||
    blockWithTextChanged?.length ||
    isSynthesisInProgress ||
    isFilteringBlockStatus ||
    isFilteringBlockVoice ||
    !!selectedBlockIds?.length ||
    isPresentationGeneratingNarration;

  const dubbingBlocksChanged = useMemo(() => {
    if (product !== PROJECT_PRODUCT.DUBBING) return [];
    return getDubbingBlockChanged(blockWithsText, originalBlocks);
  }, [product, blockWithsText, originalBlocks]);

  const isDisabledBtnDownloadSrt = useMemo(() => {
    if (!downloadFileSrtEnabled) return true;
    if (!audioLinkSrt) return true;
    if (status !== CONVERTING_STATUS.SUCCESS) return true;
    if (product === PROJECT_PRODUCT.DUBBING && dubbingBlocksChanged?.length > 0)
      return true;
    if (blockIds?.length !== originalBlockIds?.length) return true;

    return false;
  }, [
    downloadFileSrtEnabled,
    audioLinkSrt,
    status,
    product,
    dubbingBlocksChanged,
  ]);

  // The reason an export cannot run. It gates the button that runs the export,
  // not the button that opens the panel: behind the export-history flag that
  // panel is also the only way into the export history, which a project that
  // cannot export right now is exactly when someone wants to read.
  const isExportDisabled =
    isPresentationGeneratingNarration ||
    (product === PROJECT_PRODUCT.DUBBING
      ? isDisabledBtnDownloadSrt
      : isDisabledBtnDownload);

  const showErrorNotification = (message, params = {}) =>
    dispatch(
      actions.noti.push({
        severity: 'error',
        message: t(message, params),
      }),
    );

  const trackExport = (exportType) => trackProjectExport({ exportType });
  // New logic fix multiple block with same audio
  const handleDownloadParagraphsZipAudioV1 = async () => {
    if (isPresentationGeneratingNarration) return;
    dispatch(actions.project.setIsDownloading(true));
    const audioLinks = blockWithsText.map((block) => ({
      audioLink: block.audioLink,
      title: block.text.substring(0, 30),
      requestId: block.requestId,
      newTitle: block.title || sanitizeTitleFile(block.text || ''),
    }));

    // Check has voice audio expired
    const hasVoiceAudioExpired = blockWithsText.some((block) =>
      audioExpired(block?.audioExpiredAt),
    );
    if (hasVoiceAudioExpired) {
      showErrorNotification('someThingWrong');
      dispatch(actions.project.setIsDownloading(false));
      setIsTooltipOpen(false);
      setDownloadProgress(null);
      return;
    }

    const onProgress = (progress) => {
      setDownloadProgress(progress);
    };
    await handleDownloadParagraphsZip(audioLinks, title, audioType, onProgress);
    trackExport(EXPORT_TYPE.SEPARATE_AUDIO);
    dispatch(actions.project.setIsDownloading(false));
    setIsTooltipOpen(false);
    setDownloadProgress(null);
  };

  const handleDownloadParagraphsZipAudioV2 = () => {
    if (isPresentationGeneratingNarration) return;

    // Check has voice audio expired
    const hasVoiceAudioExpired = blockWithsText.some((block) =>
      audioExpired(block?.audioExpiredAt),
    );
    if (hasVoiceAudioExpired) {
      showErrorNotification('someThingWrong');
      setIsTooltipOpen(false);
      return;
    }

    // startExport automatically close tooltip when export success
    const { startExport } = separateAudioExport;
    startExport();
  };

  const safeHandleDownloadParagraphsZipAudio = async () => {
    if (useDownloadSeparateAudioV2) {
      handleDownloadParagraphsZipAudioV2();
    } else {
      handleDownloadParagraphsZipAudioV1();
    }
  };

  const handleDownloadSingleFileV1 = async () => {
    if (isPresentationGeneratingNarration) return;
    try {
      dispatch(actions.project.setIsDownloading(true));
      // Under auto-save the content is already persisted, so skip the redundant
      // full save before download.
      if (!autoSaveEnabled) await saveProject();
      dispatch(actions.project.updateOriginalBlocks());
      const response = await apis.project.getJoinedAudio(
        projectId,
        AUDIO_MODE.JOINED,
      );
      const audioJoined = response?.result?.audioLink;
      if (response.status && audioJoined) {
        const downloadAudioLink = audioJoined;
        const downloadAudioName = getFileName(audioJoined);
        downloadFileByUrl(downloadAudioLink, downloadAudioName);
        trackExport(EXPORT_TYPE.JOIN_AUDIO);
      }
    } finally {
      dispatch(actions.project.setIsDownloading(false));
      setIsTooltipOpen(false);
    }
  };

  const handleDownloadSingleFileV2 = async () => {
    if (isPresentationGeneratingNarration) return;

    if (!autoSaveEnabled) await saveProject();
    dispatch(actions.project.updateOriginalBlocks());

    const { startExport } = singleFileExport;
    startExport();
  };

  const safeHandleDownloadSingleFile = async () => {
    if (useDownloadSingleAudioV2) {
      handleDownloadSingleFileV2();
    } else {
      handleDownloadSingleFileV1();
    }
  };

  const handleDownloadSrtFile = async () => {
    // The backend builds the file now, so the subtitle download is an export
    // like any other: it is tracked, named by BR_5 and kept in the history.
    if (exportHistoryEnabled) {
      srtExport.startExport();
      return;
    }

    try {
      dispatch(actions.project.setIsDownloading(true));
      const blockToSRT = (block, index) =>
        `${index}\n${block.startTime} --> ${block.endTime}\n${block.text}\n\n`;

      const srtContent = blockWithsText
        .map((block, index) => blockToSRT(block, index + 1))
        .join('');

      const blob = new Blob([srtContent], {
        type: 'text/plain;charset=utf-8;',
      });

      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${title}.srt`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      trackExport(EXPORT_TYPE.SRT);
    } finally {
      dispatch(actions.project.setIsDownloading(false));
    }
  };

  const handleDownloadAudioSrt = async () => {
    try {
      dispatch(actions.project.setIsDownloading(true));
      const downloadAudioLink = audioLinkSrt;
      const audioName = `Vbee_${getAudioNameSrtAudio(
        title,
        projectId,
      )}_${moment().unix()}.${audioType}`;
      downloadFileByUrl(downloadAudioLink, audioName);
      trackExport(EXPORT_TYPE.JOIN_AUDIO);
    } finally {
      dispatch(actions.project.setIsDownloading(false));
    }
  };

  const handleDownload = () => {
    if (isPresentationGeneratingNarration) return undefined;
    const options = {
      singleFile: safeHandleDownloadSingleFile,
      paragraphsZip: safeHandleDownloadParagraphsZipAudio,
      srtFile: handleDownloadSrtFile,
      audioFile: handleDownloadAudioSrt,
    };

    return options[currentStructure?.value]();
  };

  // Include export audio event when consider loading state
  const isLoading =
    isDownloading ||
    separateAudioExport.isExporting ||
    singleFileExport.isExporting ||
    srtExport.isExporting;

  const isExportingByType = {
    [EXPORT_TYPE.JOIN_AUDIO]: singleFileExport.isExporting,
    [EXPORT_TYPE.SEPARATE_AUDIO]: separateAudioExport.isExporting,
    [EXPORT_TYPE.SRT]: srtExport.isExporting,
  };

  // Flag on: one export per type at a time, so the popup only blocks the type
  // already running. Flag off: any export in flight blocks it, as before. The
  // toolbar button spins for any export either way — it reports, not blocks.
  const isExportBlocked = exportHistoryEnabled
    ? isDownloading ||
      Boolean(isExportingByType[STRUCTURE_EXPORT_TYPE[currentStructure?.value]])
    : isLoading;

  const showLoadingButton = isLoading && !isTooltipOpen;
  const showLoadingInTooltip = isExportBlocked && isTooltipOpen;

  // Only show percentage when download paragraphs zip using v1 function
  const showPercentage =
    currentStructure?.value === 'paragraphsZip' && !useDownloadSeparateAudioV2;

  return (
    <RcTooltip
      placement="bottomRight"
      overlay={
        <ExportPanel isOpen={isTooltipOpen}>
          <OverLayComponent
            onChangeStructure={setCurrentStructure}
            onChangeFormat={setCurrentFormat}
            handleDownload={handleDownload}
            currentStructure={currentStructure}
            currentFormat={currentFormat}
            showLoadingInTooltip={showLoadingInTooltip}
            downloadProgress={downloadProgress}
            currentStructureValue={currentStructure?.value}
            showPercentage={showPercentage}
            isExportDisabled={isExportDisabled}
          />
        </ExportPanel>
      }
      trigger={['click']}
      showArrow={false}
      align={{
        offset: [0, 5],
      }}
      onVisibleChange={handleTooltipVisibleChange}
      visible={isTooltipOpen}
      destroyTooltipOnHide
    >
      <Button
        startIcon={
          !showLoadingButton && (
            <DownloadRoundedIcon style={{ fontSize: '24px' }} />
          )
        }
        className="download-btn"
        variant="contained"
        disabled={isExportDisabled && !exportHistoryEnabled}
        onClick={() => setIsTooltipOpen(!isTooltipOpen)}
      >
        <DownloadButtonContent
          showLoadingInTooltip={showLoadingButton}
          downloadProgress={downloadProgress}
          showPercentage={showPercentage}
        />
      </Button>
    </RcTooltip>
  );
};

export default DownloadButton;
